// src/pages/admin/AdminLayout.js
//
// Layout del panel SUPERADMIN: barra lateral con navegación entre
// Usuarios / Negocios / Vecis, header con nombre + botón cerrar sesión.

import React from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, IconButton, Avatar, Menu, MenuItem, Divider,
} from '@mui/material';
import PeopleIcon        from '@mui/icons-material/People';
import StorefrontIcon    from '@mui/icons-material/Storefront';
import PersonIcon        from '@mui/icons-material/Person';
import DashboardIcon     from '@mui/icons-material/Dashboard';
import KeyIcon           from '@mui/icons-material/Key';
import DescriptionIcon   from '@mui/icons-material/Description';
import SupportAgentIcon  from '@mui/icons-material/SupportAgent';
import AssignmentIcon    from '@mui/icons-material/Assignment';
import ReceiptLongIcon   from '@mui/icons-material/ReceiptLong';
import PaymentsIcon      from '@mui/icons-material/Payments';
import AssessmentIcon    from '@mui/icons-material/Assessment';
import SettingsIcon      from '@mui/icons-material/Settings';
import ReportIcon        from '@mui/icons-material/Report';
import AutorenewIcon     from '@mui/icons-material/Autorenew';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import HistoryIcon        from '@mui/icons-material/History';
import LogoutIcon        from '@mui/icons-material/Logout';

import { useApp } from '../../context/AppContext';

const drawerWidth = 240;

const items = [
  { path: '/admin',              label: 'Panel',         icon: <DashboardIcon /> },
  { path: '/admin/usuarios',     label: 'Usuarios',      icon: <PeopleIcon /> },
  { path: '/admin/negocios',     label: 'Negocios',      icon: <StorefrontIcon /> },
  { path: '/admin/validacion',   label: 'Validación',    icon: <AssignmentIcon /> },
  { path: '/admin/vecis',        label: 'Vecis',         icon: <PersonIcon /> },
  { path: '/admin/guias',        label: 'Guías',         icon: <SupportAgentIcon /> },
  { path: '/admin/recomendaciones', label: 'Recomendaciones', icon: <AddLocationAltIcon /> },
  { path: '/admin/moderacion',   label: 'Moderación',    icon: <ReportIcon /> },
  { path: '/admin/suscripciones', label: 'Suscripciones', icon: <AutorenewIcon /> },
  { path: '/admin/facturas',     label: 'Cuentas de cobro', icon: <ReceiptLongIcon /> },
  { path: '/admin/liquidaciones', label: 'Liquidaciones', icon: <PaymentsIcon /> },
  { path: '/admin/resultados',   label: 'Resultados',    icon: <AssessmentIcon /> },
  { path: '/admin/configuracion', label: 'Configuración', icon: <SettingsIcon /> },
  { path: '/admin/auditoria',    label: 'Auditoría',     icon: <HistoryIcon /> },
  { path: '/admin/secretos',     label: 'Secretos',      icon: <KeyIcon /> },
  { path: '/admin/proyecto',     label: 'Proyecto',      icon: <DescriptionIcon /> },
];

export default function AdminLayout() {
  const { sesionBackend, cerrarSesionBackend } = useApp();
  const location = useLocation();
  const navigate = useNavigate();

  const [menuAnchor, setMenuAnchor] = React.useState(null);

  const salir = async () => {
    setMenuAnchor(null);
    await cerrarSesionBackend();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px`,
          bgcolor: '#fff', color: '#0F4C3A', borderBottom: '1px solid #eee',
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontFamily: 'Fraunces', fontWeight: 700 }}>
            HolaVeci — Admin
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            {sesionBackend?.correo}
          </Typography>
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#0F4C3A' }}>
              {(sesionBackend?.nombre?.[0] || 'A').toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            <MenuItem onClick={salir}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Cerrar sesión
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth, flexShrink: 0,
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box',
            bgcolor: '#0F4C3A', color: '#fff' },
        }}
      >
        <Toolbar>
          <Typography variant="h5" sx={{ fontFamily: 'CarterOne', letterSpacing: 0.5 }}>
            ¡Hola veci!
          </Typography>
        </Toolbar>
        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.15)' }} />
        <List>
          {items.map((it) => {
            // La ruta /admin/negocios/:id/validar pertenece al flujo de
            // validación humana (D-18), no a "Negocios". Cuando el
            // validador está en esa vista, el ítem 'Validación' debe
            // quedar resaltado (no 'Negocios' que solo aplica al listado
            // general).
            const enValidacionDeNegocio = /^\/admin\/negocios\/\d+\/validar/
              .test(location.pathname);
            const activo = it.path === '/admin'
              ? location.pathname === '/admin'
              : enValidacionDeNegocio
                ? it.path === '/admin/validacion'
                : location.pathname.startsWith(it.path);
            return (
              <ListItem key={it.path} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={it.path}
                  sx={{
                    color: activo ? '#F8F5EF' : 'rgba(255,255,255,0.75)',
                    bgcolor: activo ? 'rgba(255,255,255,0.08)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit' }}>{it.icon}</ListItemIcon>
                  <ListItemText primary={it.label} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: '#F8F5EF', minHeight: '100vh', p: 3, mt: 8 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
